import { dateOf, num, table, tile } from './sbchart.ts';
import { readRecentDays, RECENT_DAY_LIMIT } from './sbrecent-days.ts';

type Timing = Record<string, unknown> | null | undefined;
const value = (t: Timing, key: string): number | null => typeof t?.[key] === 'number' && Number.isFinite(t[key]) && (t[key] as number) >= 0 ? t[key] as number : null;
const duration = (t: Timing, key: string): string => {
  const ms = value(t, key);
  return ms == null ? '未提供' : ms >= 1000 ? `${num(ms / 1000, 2)} 秒` : `${num(ms, 2)} ms`;
};
const count = (t: Timing, key: string): string => {
  const n = value(t, key);
  return n == null ? '未提供' : num(n);
};

export function recentDayTimingCard(t: Timing): string {
  const r = readRecentDays(t?.recentDays);
  return tile('近期完整日均', !r ? '未记录' : !r.count ? '尚无样本' : `${num(r.meanMs!, 2)} ms`,
    !r ? '此线程未提供近期窗口；不补造旧采样' : `最近 ${num(r.count)} / ${r.limit} 个完整日；含助手与归档等待`);
}

export function recentDayTimingHTML(t: Timing): string {
  const r = readRecentDays(t?.recentDays);
  if (!r) return '<p class="meta">此模拟线程未记录近期完整日耗时，旧线程的累计平均不能补算近期窗口。</p>';
  if (!r.count) return '<p class="meta">尚无完整日采样；只在本城实际完成一天后记录，暂停和只读查询不增加样本。</p>';
  const ms = (n: number) => `${num(n, 2)} ms`;
  return table(['近期完整日', `最近最多 ${RECENT_DAY_LIMIT} 日实测`], [
    ['实际样本数', `${num(r.count)} / ${r.limit}`],
    ['采样游戏日期', `${dateOf(r.fromDay!)} 至 ${dateOf(r.toDay!)}`],
    ['近期均值', ms(r.meanMs!)], ['P50', ms(r.p50Ms!)], ['P95', ms(r.p95Ms!)], ['窗口最大值', ms(r.maxMs!)],
  ]) + '<p class="meta">仅记录最近实际完成的游戏日；不含两天之间的暂停时间。每个样本沿用完整单日推进计时，含该日助手和归档等待，不是纯CPU耗时，也不是整城提速。P50、P95采用最近秩（排序后第 ceil(比例 × 样本数) 项）；少量样本时不能代表长期分布。换城后重新采样。</p>';
}

/** 都是实测墙钟时间；等待可与助手计算重叠，不能相加计算CPU使用率。 */
export function engineTimingHTML(t: Timing): string {
  if (!t) return '<p class="meta">此模拟线程尚未提供分阶段耗时；等待下一次城市更新。</p>';
  return table(['阶段', '本城累计实测'], [
    ['引擎线性内存', value(t, 'memoryBytes') == null ? '未提供' : `${num(value(t, 'memoryBytes')! / (1024 * 1024), 2)} MiB`],
    ['热视图调用期间内存扩展', value(t, 'viewMemoryGrowthBytes') == null ? '未提供' : `${num(value(t, 'viewMemoryGrowthBytes')! / (1024 * 1024), 2)} MiB · ${count(t, 'viewMemoryGrowthCount')} 次`],
    ['开局初始化', duration(t, 'initMs')],
    ['同步推进与数据处理', duration(t, 'computeMs')],
    ['计算助手剩余等待', duration(t, 'assistantWaitMs')],
    ['打开历史档案', duration(t, 'archiveOpenMs')],
    ['归档读出与等待', duration(t, 'archiveFlushMs')],
    ['热视图计算与编码（WASM）', duration(t, 'viewWasmMs')],
    ['热视图 UTF-8 解码', duration(t, 'viewDecodeMs')],
    ['热视图调用 / 异常', `${count(t, 'viewCalls')} / ${count(t, 'viewErrors')}`],
    ['热视图成功返回的数据量', value(t, 'viewBytes') == null ? '未提供' : `${num(value(t, 'viewBytes')! / (1024 * 1024), 2)} MiB（UTF-8）`],
    ['实际热查询批次 / 异常', `${count(t, 'queryCount')} / ${count(t, 'queryErrors')}`],
    ['热查询排队累计 / 最长', `${duration(t, 'queryQueueMs')} / ${duration(t, 'maxQueryQueueMs')}`],
    ['热查询回信同步入队', duration(t, 'queryPostMs')],
    ['历史读取与合并等待', duration(t, 'archiveReadMs')],
    ['实际历史读取 / 失败', `${count(t, 'archiveReadCount')} / ${count(t, 'archiveReadErrors')}`],
    ['线程让步等待', `${duration(t, 'yieldWaitMs')} · ${value(t, 'yieldCount') == null ? '次数未提供' : `${num(value(t, 'yieldCount')!)} 次`}`],
    ['最长一次让步等待', duration(t, 'maxYieldWaitMs')],
    ['最近 / 最长完整单日推进', `${duration(t, 'lastDayMs')} / ${duration(t, 'maxDayMs')}`],
    ['本城经过时间（包括暂停）', duration(t, 'elapsedMs')],
  ]) + '<p class="meta">线性内存包含仍在使用和已经释放、留待复用的空间，是本引擎实例的分配高水位，不等于存活对象占用，不含页面及其他独立引擎的内存；多线程共享的同一块内存只计一次。热视图扩展只计调用期间实际增加的内存，不是每次查询分配的总量，不能据此把增长全部归因于历史记录。同步推进包含模拟与快照处理，热视图计算与编码另计（包括面板查询及城市推送）。查询排队包含等完整日和此前操作；回信入队不含传输、界面解析或绘制，本次入队耗时在后续统计快照反映。历史读取包含索引读取、等待和结果合并，不含读取前的强制写档。归档包含读出记录、交给浏览器，以及积压过多、需要全部写完或故障时的等待。单日推进包含异步等待与归档，最长一次让步反映调度等待。这些时间可能重叠，不能相加当作CPU占用率，也不是整城提速百分比。</p>';
}

export function bootTimingText(t: Timing): string {
  if (!t || value(t, 'elapsedMs') == null) return '';
  return `实测已用 ${duration(t, 'elapsedMs')} · 同步推进 ${duration(t, 'computeMs')} · 归档 ${duration(t, 'archiveFlushMs')} · 让步等待 ${duration(t, 'yieldWaitMs')}`;
}

export function archiveWriteHTML(t: Timing): string {
  if (!t) return '<p class="meta">此存档线程尚未提供写入分项。</p>';
  return table(['归档写入阶段', '本城累计实测'], [
    ['事务尝试 / 成功 / 失败', `${count(t, 'attempts')} / ${count(t, 'completed')} / ${count(t, 'failed')}`],
    ['最近 / 最大批行数', `${count(t, 'lastRows')} / ${count(t, 'maxRows')}`],
    ['记录与引用键准备', duration(t, 'prepareMs')],
    ['同步写请求入队', duration(t, 'putMs')],
    ['入队后至事务终态等待', duration(t, 'commitWaitMs')],
    ['最近 / 最长终态等待', `${duration(t, 'lastCommitWaitMs')} / ${duration(t, 'maxCommitWaitMs')}`],
    ['请求 / 入队 / 成功行数（含重试）', `${count(t, 'rowsRequested')} / ${count(t, 'rowsSubmitted')} / ${count(t, 'rowsCommitted')}`],
    ['提交的索引键条目（含重试）', count(t, 'indexKeysSubmitted')],
    ['城市元数据写请求', count(t, 'metadataPuts')],
  ]) + '<p class="meta">准备与同步入队是墙钟计时；终态等待包括事务排队、浏览器存储处理、调度及失败超时，不是纯磁盘时间。成功仅按事务完成确认，失败或超时后的迟到回调不补算成功。初始化的零行登记也算一次事务；重试行数和逻辑索引键会重复计数，不是唯一历史条数或实际磁盘读写次数。</p>';
}
